import { useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  Home, AlertTriangle, ClipboardCheck, Award, MoreHorizontal,
  LogOut, X, FileText, ShieldCheck, Users, Settings, ChevronRight, ChevronLeft, CheckSquare,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'
import OfflineBanner from './OfflineBanner'

const TABS = [
  { to: '/m/home',      icon: Home,          label: 'Home',      accent: '#00D4AA' },
  { to: '/m/incidents', icon: AlertTriangle,  label: 'Incidents', accent: '#FF3B5C' },
  { to: '/m/audits',    icon: ClipboardCheck, label: 'Audits',    accent: '#00D4AA' },
  { to: '/m/licences',  icon: Award,          label: 'Licences',  accent: '#FF9500' },
]

const MORE_ITEMS = [
  { to: '/m/lone-worker',        icon: ShieldCheck, label: 'Lone Worker',        accent: '#FF3B5C' },
  { to: '/m/permits',            icon: FileText,    label: 'Permits to Work',    accent: '#0A84FF' },
  { to: '/m/corrective-actions', icon: CheckSquare,  label: 'Corrective Actions', accent: '#00D4AA' },
  { to: '/m/workforce',          icon: Users,       label: 'Workforce',          accent: '#BF5AF2' },
  { to: '/m/settings',           icon: Settings,    label: 'Settings',           accent: 'rgba(255,255,255,0.45)' },
]

// Ordered most-specific first so startsWith matches correctly
const SUB_PAGES: Array<{ prefix: string; title: string; parent: string }> = [
  { prefix: '/m/lone-worker/active',  title: 'Active Session',     parent: '/m/home' },
  { prefix: '/m/lone-worker',         title: 'Lone Worker',        parent: '/m/home' },
  { prefix: '/m/incidents/new',       title: 'Report Incident',    parent: '/m/incidents' },
  { prefix: '/m/incidents/',          title: 'Incident',           parent: '/m/incidents' },
  { prefix: '/m/audits/',             title: 'Audit',              parent: '/m/audits' },
  { prefix: '/m/permits',             title: 'Permits to Work',    parent: '/m/home' },
  { prefix: '/m/corrective-actions',  title: 'Corrective Actions', parent: '/m/home' },
  { prefix: '/m/workforce',           title: 'Workforce',          parent: '/m/home' },
  { prefix: '/m/settings',            title: 'Settings',           parent: '/m/home' },
]

const NAV_BG = '#070B14'
const DRAWER_BG = '#0F1823'
const DIM = 'rgba(255,255,255,0.28)'

export default function MobileLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)

  const handleLogout = async () => {
    try { await api.post('/auth/logout') } catch {}
    logout()
    navigate('/login')
  }

  const moreActive = MORE_ITEMS.some(i => location.pathname.startsWith(i.to))
  const mainTabPaths = TABS.map(t => t.to)
  const isMainTab = mainTabPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
  const subPage = SUB_PAGES.find(s => location.pathname.startsWith(s.prefix))

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Header */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          background: NAV_BG,
          paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
          paddingBottom: '0.625rem',
          paddingLeft: '1.25rem',
          paddingRight: '1.25rem',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        {!isMainTab && subPage ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(subPage.parent)}
              style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            >
              <ChevronLeft size={20} color="#FFFFFF" />
            </button>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#FFFFFF' }}>{subPage.title}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <ShieldCheck size={17} color="#00D4AA" strokeWidth={2.5} />
            <span style={{ fontWeight: 800, fontSize: 14, color: '#FFFFFF', letterSpacing: '-0.01em' }}>
              HSEQ
            </span>
            <span style={{ fontWeight: 400, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
              intelligence
            </span>
          </div>
        )}
        <div className="flex items-center gap-2.5">
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{user?.firstName}</span>
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: 'none',
              borderRadius: '50%',
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <LogOut size={13} color="rgba(255,255,255,0.45)" />
          </button>
        </div>
      </div>

      {/* Page content */}
      <main className="flex-1 overflow-y-auto">
        <OfflineBanner />
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav
        className="flex-shrink-0"
        style={{
          background: NAV_BG,
          borderTop: '1px solid rgba(255,255,255,0.07)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          minHeight: 64,
          position: 'relative',
          zIndex: 200,
        }}
      >
        <div className="flex h-16">
          {TABS.map(({ to, icon: Icon, label, accent }) => (
            <NavLink
              key={to}
              to={to}
              className="flex-1 flex flex-col items-center justify-center gap-0.5"
              style={{ background: 'none', textDecoration: 'none' }}
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={21}
                    color={isActive ? accent : DIM}
                    strokeWidth={isActive ? 2.5 : 1.8}
                  />
                  <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: isActive ? accent : DIM,
                    letterSpacing: '0.01em',
                  }}>
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          ))}

          {/* More tab */}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <MoreHorizontal size={21} color={moreActive ? '#BF5AF2' : DIM} strokeWidth={1.8} />
            <span style={{ fontSize: 10, fontWeight: 600, color: moreActive ? '#BF5AF2' : DIM }}>More</span>
          </button>
        </div>
      </nav>

      {/* More drawer */}
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={() => setMoreOpen(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl"
            style={{
              background: DRAWER_BG,
              paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
              border: '1px solid rgba(255,255,255,0.07)',
              borderBottom: 'none',
            }}
          >
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)' }} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px 12px' }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#FFFFFF' }}>More</span>
              <button
                onClick={() => setMoreOpen(false)}
                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <X size={16} color="rgba(255,255,255,0.5)" />
              </button>
            </div>

            <div style={{ paddingBottom: 8 }}>
              {MORE_ITEMS.map(({ to, icon: Icon, label, accent }) => (
                <button
                  key={to}
                  onClick={() => { setMoreOpen(false); navigate(to) }}
                  style={{
                    background: 'none',
                    border: 'none',
                    width: '100%',
                    padding: '12px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: `${accent}26`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={20} color={accent} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF', flex: 1 }}>{label}</span>
                  <ChevronRight size={15} color="rgba(255,255,255,0.2)" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
